import React, { useContext, useMemo, useState } from "react";
import { matchPath, type PathMatch } from "react-router";

import type { ModelContext } from "@src/features/chrome";
import { useTabId } from "@src/features/chrome/context/TabIdContext";
import { useAppBusEvent } from "@src/features/sse/hooks/useAppBusEvent";
import { useGetUrlContexts } from "@src/shared/hooks/useGetUrlContexts";
import usePrevious from "@src/shared/hooks/usePrevious";

type UrlContextValue = {
  url: string;
  context: ModelContext | null;
  pathMatch: PathMatch | null;
  urlDidChange: boolean;
};

const UrlContext = React.createContext<UrlContextValue | null>(null);

export const UrlContextProvider = ({ children }: React.PropsWithChildren) => {
  const tabId = useTabId();

  const [url, setUrl] = useState("");

  const previousUrl = usePrevious(url);

  const urlDidChange = Boolean(url && previousUrl && url !== previousUrl);

  const { data: response } = useGetUrlContexts(
    { domain: url },
    {
      query: {
        enabled: Boolean(url),
      },
    },
  );

  const pathname = useMemo(() => {
    if (!url) return null;

    return new URL(url).pathname;
  }, [url]);

  const { context, pathMatch } = useMemo(() => {
    if (!pathname || !response?.data.contexts) {
      return { context: null, pathMatch: null };
    }

    for (const ctx of response.data.contexts) {
      for (const pattern of ctx.patterns) {
        const match = matchPath(pattern.pattern, pathname);
        if (match) {
          return { context: ctx, pathMatch: match };
        }
      }
    }

    return { context: null, pathMatch: null };
  }, [response, pathname]);

  useAppBusEvent("chrome.url_changed", (p) => {
    if (tabId == null || p.tabId !== tabId) return;

    setUrl?.(p.url);
  });

  return (
    <UrlContext.Provider value={{ url, context, pathMatch, urlDidChange }}>
      {children}
    </UrlContext.Provider>
  );
};

export function useUrlContext(): UrlContextValue {
  const context = useContext(UrlContext);

  return (
    context ?? { url: "", context: null, pathMatch: null, urlDidChange: false }
  );
}
